/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { InputAction } from "../enums/InputAction";
import { PlatformKeyMapper } from "./PlatformKeyMapper";
import { PointerMapper } from "./PointerMapper";
import { InterceptorChain } from "./InterceptorChain";
import { ScreenRegistry } from "./ScreenRegistry";
import type {
  TInputEvent,
  InputSource,
  InputEventType
} from "./types/TInputEvent";
import type { NavigationEngineConfig } from "./types/TNavigationEngine";
import type { IScreenController } from "./interfaces/IScreenController";
import type { INavigationEngine } from "./interfaces/INavigationEngine";

/**
 * Unified entry point for the navigation system.
 *
 * All user input — keyboard, D-pad, remote, mouse, pointer — flows through
 * a single pipeline:
 *   1. Raw DOM event → PlatformKeyMapper / PointerMapper → InputAction
 *   2. InputAction + metadata → InterceptorChain (middleware)
 *   3. Post-interceptor event → ScreenRegistry → active ScreenController
 */
export class NavigationEngine implements INavigationEngine {
  private static TAG: string = "NavigationEngine";

  /**
   * `<input>` element `type` values that do NOT accept free-form text entry.
   * These behave like discrete controls (toggle, activate, pick) rather than
   * text fields, so keys such as Enter (NAV_OK) must still be routed through
   * the navigation pipeline instead of being left to native browser handling.
   */
  private static readonly NON_TEXT_INPUT_TYPES: ReadonlySet<string> = new Set([
    "checkbox",
    "radio",
    "button",
    "submit",
    "reset",
    "range",
    "color",
    "file",
    "image",
    "hidden"
  ]);

  private readonly mKeyMapper: PlatformKeyMapper;
  private readonly mPointerMapper: PointerMapper;
  private readonly mInterceptors: InterceptorChain;
  private readonly mRegistry: ScreenRegistry;
  private readonly mConfig: NavigationEngineConfig;

  /**
   * FR-05 fix — extracted from config so config remains truly readonly.
   * `setActiveScreenGetter()` mutates this field, not the config object.
   */
  private mGetActiveScreen: () => string;

  private mIsListening: boolean = false;

  private mBoundKeyDown: (e: KeyboardEvent) => void;
  private mBoundKeyUp: (e: KeyboardEvent) => void;
  private mBoundClick: (e: MouseEvent) => void;
  private mBoundContextMenu: (e: MouseEvent) => void;
  private mBoundWheel: (e: WheelEvent) => void;
  private mBoundMouseOver: (e: MouseEvent) => void;
  private mBoundMouseOut: (e: MouseEvent) => void;

  constructor(config: NavigationEngineConfig) {
    this.mConfig = { enablePointer: true, ...config };
    this.mGetActiveScreen = config.getActiveScreen;
    this.mKeyMapper = new PlatformKeyMapper(config.platform);
    this.mPointerMapper = new PointerMapper();
    this.mInterceptors = new InterceptorChain();
    this.mRegistry = new ScreenRegistry();
    this.mBoundKeyDown = this.handleKeyEvent.bind(this, "keydown");
    this.mBoundKeyUp = this.handleKeyEvent.bind(this, "keyup");
    this.mBoundClick = this.handleMouseEvent.bind(this);
    this.mBoundContextMenu = this.handleMouseEvent.bind(this);
    this.mBoundWheel = this.handleWheelEvent.bind(this);
    this.mBoundMouseOver = this.handleMouseEvent.bind(this);
    this.mBoundMouseOut = this.handleMouseEvent.bind(this);
  }

  // ────────────────────────────────────────────
  //  Keyboard Pipeline
  // ────────────────────────────────────────────

  /**
   * Returns true when the keyboard event originated inside a text-entry
   * element (input, textarea, or any contentEditable element).
   *
   * When true, keys that have native text-editing meaning (typing characters,
   * Backspace, Delete, arrows for cursor movement, etc.) must NOT have their
   * default browser action prevented and must NOT be routed into the navigation
   * pipeline — the browser/input element handles them directly.
   *
   * Pure structural navigation actions (NAV_UP / NAV_DOWN / NAV_LEFT /
   * NAV_RIGHT when they come from arrow keys) are still allowed through so
   * that the D-pad can move away from the focused field, but the browser's
   * cursor-movement behaviour is suppressed for them.
   */
  private isTextEditTarget(raw: KeyboardEvent): boolean {
    const target = raw.target;
    if (target === null || target === undefined) {
      return false;
    }
    const el = target as HTMLElement;
    const tag = el.tagName;
    if (tag === "TEXTAREA") {
      return true;
    }
    if (tag === "INPUT") {
      const inputType: string = (el as HTMLInputElement).type.toLowerCase();
      return !NavigationEngine.NON_TEXT_INPUT_TYPES.has(inputType);
    }
    if (el.isContentEditable === true) {
      return true;
    }
    return false;
  }

  /**
   * Returns true for actions that are purely structural navigation and should
   * always route through the pipeline regardless of focus target.
   * All other actions (numbers, Backspace → BACK, etc.) should be skipped
   * when the target is a text-entry element so the browser handles them.
   */
  private isStructuralNavAction(action: InputAction): boolean {
    return (
      action === InputAction.NAV_UP ||
      action === InputAction.NAV_DOWN ||
      action === InputAction.NAV_LEFT ||
      action === InputAction.NAV_RIGHT
    );
  }

  /**
   * True only for the literal Backspace character key (key "Backspace" or
   * keyCode 8) — the one case where BACK must be suppressed inside a text
   * field so the browser can delete a character instead of navigating away.
   * A real remote's BACK button (Escape, XF86Back, keyCode 10009, etc.) is
   * always a distinct key/code from Backspace, so it is never caught here.
   */
  private isNativeBackspaceKey(raw: KeyboardEvent): boolean {
    return raw.key === "Backspace" || raw.keyCode === 8;
  }

  private handleKeyEvent(
    eventType: "keydown" | "keyup",
    raw: KeyboardEvent
  ): void {
    const action = this.mKeyMapper.resolve(raw);
    if (action === InputAction.UNKNOWN) {
      return;
    }

    // When the event originates inside a text-entry element, only structural
    // navigation keys (arrow keys → NAV_*) and a genuine BACK button press
    // are routed through the pipeline. All other mapped actions (numbers,
    // the literal Backspace key, etc.) are left completely unhandled here so
    // the browser can process them natively (character insertion, deletion,
    // cursor movement inside the field). Without the BACK exception, a
    // remote's dedicated Back button would be silently swallowed whenever
    // focus sits in a text field, stranding the user on that screen.
    const isBackButtonPress: boolean =
      action === InputAction.BACK && !this.isNativeBackspaceKey(raw);
    if (
      this.isTextEditTarget(raw) &&
      !this.isStructuralNavAction(action) &&
      !isBackButtonPress
    ) {
      return;
    }

    // FR-04 fix — only preventDefault on keydown. Calling it on keyup is a
    // no-op for most keys, adds overhead, and blocks browser-native keyup
    // behaviours during development/debugging.
    if (eventType === "keydown") {
      raw.preventDefault();
    }

    const event = this.buildInputEvent(action, "keyboard", eventType, raw, {
      repeat: raw.repeat
    });

    this.processAndRoute(event);
  }

  // ────────────────────────────────────────────
  //  Pointer/Mouse Pipeline
  // ────────────────────────────────────────────

  private handleMouseEvent(raw: MouseEvent): void {
    const action = this.mPointerMapper.resolveMouseEvent(raw);
    if (action === InputAction.UNKNOWN) {
      return;
    }

    if (raw.type === "contextmenu") {
      raw.preventDefault();
    }

    const event = this.buildInputEvent(
      action,
      "pointer",
      raw.type as InputEventType,
      raw,
      {
        position: {
          clientX: raw.clientX,
          clientY: raw.clientY,
          target: raw.target
        }
      }
    );

    this.processAndRoute(event);
  }

  private handleWheelEvent(raw: WheelEvent): void {
    const action = this.mPointerMapper.resolveWheelEvent(raw);
    if (action === InputAction.UNKNOWN) {
      return;
    }

    raw.preventDefault();

    const event = this.buildInputEvent(action, "pointer", "wheel", raw, {
      position: {
        clientX: raw.clientX,
        clientY: raw.clientY,
        target: raw.target
      }
    });

    this.processAndRoute(event);
  }

  // ────────────────────────────────────────────
  //  Shared Pipeline
  // ────────────────────────────────────────────

  private buildInputEvent(
    action: InputAction,
    source: InputSource,
    eventType: InputEventType,
    raw: Event,
    extra: Partial<TInputEvent> = {}
  ): TInputEvent {
    const modRaw = raw as KeyboardEvent | MouseEvent;
    return {
      action,
      source,
      eventType,
      raw,
      repeat: false,
      // FR-06 fix — use the native event timestamp (DOMHighResTimeStamp) so
      // that ScrollThrottleInterceptor and any other time-sensitive interceptors
      // measure from when the DOM event actually occurred, not from when it
      // reached this handler.  On slow SmartTV hardware the processing delay
      // can be non-trivial.
      timestamp: raw.timeStamp,
      stopped: false,
      meta: {},
      modifiers: {
        ctrl: modRaw.ctrlKey,
        shift: modRaw.shiftKey,
        alt: modRaw.altKey,
        meta: modRaw.metaKey
      },
      ...extra
    };
  }

  private processAndRoute(event: TInputEvent): void {
    this.mInterceptors.process(event);
    if (event.stopped) {
      return;
    }

    const activeScreen = this.mGetActiveScreen();
    const isHandled = this.mRegistry.routeInput(activeScreen, event);

    if (!isHandled && this.mConfig.onUnhandledInput) {
      this.mConfig.onUnhandledInput(event);
    }
  }

  // ────────────────────────────────────────────
  //  Public API
  // ────────────────────────────────────────────

  /**
   * Attaches all DOM event listeners and begins processing input events.
   * Idempotent — safe to call multiple times.
   */
  public start(): void {
    if (this.mIsListening) {
      return;
    }

    document.addEventListener("keydown", this.mBoundKeyDown, { capture: true });
    document.addEventListener("keyup", this.mBoundKeyUp, { capture: true });

    if (this.mConfig.enablePointer) {
      document.addEventListener("click", this.mBoundClick, { capture: true });
      document.addEventListener("contextmenu", this.mBoundContextMenu, {
        capture: true
      });
      document.addEventListener("wheel", this.mBoundWheel, {
        capture: true,
        passive: false
      });
      document.addEventListener("mouseover", this.mBoundMouseOver, {
        capture: true
      });
      document.addEventListener("mouseout", this.mBoundMouseOut, {
        capture: true
      });
    }

    this.mIsListening = true;
  }

  /**
   * Removes all DOM event listeners. Idempotent — safe to call multiple times.
   */
  public stop(): void {
    if (!this.mIsListening) {
      return;
    }

    document.removeEventListener("keydown", this.mBoundKeyDown, {
      capture: true
    });
    document.removeEventListener("keyup", this.mBoundKeyUp, { capture: true });

    // FR-07 fix — only remove pointer listeners when they were actually added.
    // removeEventListener on a never-added listener is a safe no-op, but the
    // symmetry makes intent clear and avoids confusion during debugging.
    if (this.mConfig.enablePointer) {
      document.removeEventListener("click", this.mBoundClick, {
        capture: true
      });
      document.removeEventListener("contextmenu", this.mBoundContextMenu, {
        capture: true
      });
      document.removeEventListener("wheel", this.mBoundWheel, {
        capture: true
      });
      document.removeEventListener("mouseover", this.mBoundMouseOver, {
        capture: true
      });
      document.removeEventListener("mouseout", this.mBoundMouseOut, {
        capture: true
      });
    }

    this.mIsListening = false;
  }

  /**
   * Returns the interceptor chain, allowing callers to add or remove
   * middleware interceptors.
   *
   * @returns The shared {@link InterceptorChain} instance.
   */
  public getInterceptors(): InterceptorChain {
    return this.mInterceptors;
  }

  /**
   * Returns the screen registry that maps screen names to controllers.
   *
   * @returns The shared {@link ScreenRegistry} instance.
   */
  public getRegistry(): ScreenRegistry {
    return this.mRegistry;
  }

  /**
   * Returns the platform key mapper used by this engine.
   *
   * @returns The shared {@link PlatformKeyMapper} instance.
   */
  public getKeyMapper(): PlatformKeyMapper {
    return this.mKeyMapper;
  }

  /**
   * Returns the pointer/mouse mapper used by this engine.
   *
   * @returns The shared {@link PointerMapper} instance.
   */
  public getPointerMapper(): PointerMapper {
    return this.mPointerMapper;
  }

  /**
   * Returns the {@link IScreenController} registered under the currently
   * active screen name, or `undefined` if no matching controller is found.
   *
   * @returns The active screen controller, or `undefined`.
   */
  public getActiveScreenController(): IScreenController | undefined {
    const activeScreen = this.mGetActiveScreen();
    return this.mRegistry.getController(activeScreen);
  }

  /**
   * Registers a screen controller so it can receive routed input events.
   *
   * @param controller - The screen controller to register.
   */
  public registerScreen(controller: IScreenController): void {
    this.mRegistry.registerScreen(controller);
  }

  /**
   * Unregisters the screen controller with the given name so it no longer
   * receives input events.
   *
   * @param screenName - The name of the screen controller to remove.
   */
  public unregisterScreen(screenName: string): void {
    this.mRegistry.unregisterScreen(screenName);
  }

  /**
   * Stops event listening and cleans up all registered screen controllers.
   */
  public destroy(): void {
    this.stop();
    this.mRegistry.cleanup();
  }

  /**
   * Replaces the active-screen getter at runtime.
   *
   * FR-05 fix — updates the extracted `mGetActiveScreen` field rather than
   * mutating the readonly `mConfig` object, preserving true config immutability.
   *
   * @param getter - A function that returns the current active screen name.
   */
  public setActiveScreenGetter(getter: () => string): void {
    this.mGetActiveScreen = getter;
  }
}
